import { z } from 'zod'

export const loginSchema = z.object({
  email: z.string().trim().min(1, 'Username or email is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
})

export const signupSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const passwordResetRequestSchema = z.object({
  email: z.string().trim().email('Invalid email address'),
})

export const passwordResetSchema = z
  .object({
    resetToken: z.string().trim().min(1, 'Reset token is required'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

/**
 * Canonical withdraw-amount error strings, shared by {@link withdrawSchema}
 * and the dynamic withdraw-flow validation in
 * `src/features/balances/hooks/useWithdrawFlow.ts` (destination parsing and
 * balance checks cannot be expressed in a static schema, but the static
 * amount rules must not drift apart).
 */
export const withdrawAmountMessages = {
  required: 'Amount is required',
  min: 'Amount must be at least 1 sat',
  integer: 'Amount must be a whole number of sats',
} as const

export const withdrawSchema = z.object({
  amount: z
    .number()
    .int(withdrawAmountMessages.integer)
    .min(1, withdrawAmountMessages.min)
    .optional(),
  address: z.string().trim().min(1, 'Destination is required'),
})

export const autoWithdrawDestinationSchema = z.object({
  lightningAddress: z.string().trim().min(1, 'Destination is required'),
})

export const topupSchema = z.object({
  amount: z.coerce.number().min(1, 'Amount must be at least $1'),
})

export const redeemSchema = z.object({
  code: z.string().trim().min(1, 'Code is required'),
})

export type LoginInput = z.infer<typeof loginSchema>
export type SignupInput = z.infer<typeof signupSchema>
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>
export type PasswordResetInput = z.infer<typeof passwordResetSchema>
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>
export type WithdrawInput = z.infer<typeof withdrawSchema>
export type AutoWithdrawDestinationInput = z.infer<typeof autoWithdrawDestinationSchema>
export type TopupInput = z.infer<typeof topupSchema>
export type RedeemInput = z.infer<typeof redeemSchema>
